// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/tracking/ShipmentForm.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { trackingAPI, warehouseAPI, vendorsAPI } from "../../services/api";
import { useSubmit } from "../../hooks/useSubmit";
import SearchableSelect from "../../components/SearchableSelect";
import PersianDatePicker, { todayGregorian } from "../../utils/PersianDatePicker";
import { ArrowRight, Save, Plus, Trash2, AlertTriangle } from "lucide-react";

const OWNERSHIP_OPTIONS = [
  { value: "company",  label: "🏢 شرکت ما" },
  { value: "transit",  label: "🚛 ترانزیت" },
];

const STATUS_OPTIONS = [
  { value: "in_customs",  label: "در گمرک" },
  { value: "clearing",    label: "در حال ترخیص" },
  { value: "cleared",     label: "ترخیص شده" },
  { value: "distributed", label: "توزیع شده" },
];

interface Item {
  product_id: string;
  product_name: string;
  unit: string;
  quantity: string;
  notes: string;
}

const emptyItem = (): Item => ({ product_id: "", product_name: "", unit: "", quantity: "", notes: "" });

const emptyForm = {
  bar_number: "",
  invoice_number: "", // 🆕 شماره اینوایس
  entry_date: todayGregorian(),
  customs_office_id: "",
  customs_office_name: "",
  ownership_type: "company",
  transit_owner: "",
  bl_number: "",
  i_number: "",
  e_number: "",
  container_number: "",
  truck_plate: "",
  status: "in_customs",
  clearance_date: "",
  notes: "",
  attachment: "",
};

interface Props {
  shipmentId: number | null;  // null = افزودن، id = ویرایش
  onBack: () => void;
}

export default function ShipmentForm({ shipmentId, onBack }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const { submitting, submit } = useSubmit();

  const isEdit = shipmentId !== null;

  const [form, setForm] = useState<any>(emptyForm);
  const [items, setItems] = useState<Item[]>([emptyItem()]);
  const [offices, setOffices] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);  // 🆕 لیست فروشندگان
  const [autoVendorId, setAutoVendorId] = useState<string>("");  // 🆕 فروشنده انتخابی برای خرید خودکار
  const [hasAutoPurchase, setHasAutoPurchase] = useState(false);  // 🆕 آیا قبلاً خرید خودکار دارد
  const [loading, setLoading] = useState(true);
  const [hasDistributions, setHasDistributions] = useState(false);
  const [toast, setToast] = useState({ text: "", ok: true });

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [o, p, u, v] = await Promise.all([
      trackingAPI.listOffices(),
      warehouseAPI.productsList(),
      warehouseAPI.unitsList(),
      vendorsAPI.list(),  // 🆕 لیست فروشندگان
    ]);
    if (o.success) setOffices(o.offices || []);
    if (p.success) setProducts(p.products || []);
    if (u.success) setUnits(u.units || []);
    if (v.success) setVendors(v.vendors || []);  // 🆕

    if (isEdit && shipmentId) {
      const s = await trackingAPI.getShipment(shipmentId);
      if (s.success) {
        const sh = s.shipment;
        setForm({
          bar_number: sh.bar_number || "",
          invoice_number: sh.invoice_number || "", // 🆕
          entry_date: sh.entry_date,
          customs_office_id: String(sh.customs_office_id || ""),
          customs_office_name: sh.customs_office_name || "",
          ownership_type: sh.ownership_type,
          transit_owner: sh.transit_owner || "",
          bl_number: sh.bl_number || "",
          i_number: sh.i_number || "",
          e_number: sh.e_number || "",
          container_number: sh.container_number || "",
          truck_plate: sh.truck_plate || "",
          status: sh.status,
          clearance_date: sh.clearance_date || "",
          notes: sh.notes || "",
          attachment: sh.attachment || "",
        });
        setItems((sh.items || []).map((it: any) => ({
          product_id: String(it.product_id),
          product_name: it.product_name,
          unit: it.unit,
          quantity: String(it.quantity),
          notes: it.notes || "",
        })));
        setHasDistributions((sh.items || []).some((it: any) => (it.distributions || []).length > 0));
        // 🆕 آیا این بار قبلاً خرید خودکار دارد؟
        setHasAutoPurchase(!!sh.auto_purchase_id);
        if (sh.auto_purchase_vendor_id) setAutoVendorId(String(sh.auto_purchase_vendor_id));
      }
    }
    setLoading(false);
  }, [isEdit, shipmentId]);

  useEffect(() => { load(); }, [load]);

  const addItemRow = () => setItems([...items, emptyItem()]);
  const removeItemRow = (i: number) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i: number, key: keyof Item, val: string) => {
    setItems(items.map((it, idx) => {
      if (idx !== i) return it;
      const u = { ...it, [key]: val };
      if (key === "product_id") {
        const p = products.find((x: any) => String(x.id) === val);
        if (p) {
          u.product_name = p.name;
          const unitObj = units.find((un: any) => un.id === p.unit_id);
          u.unit = unitObj?.name || "";
        }
      }
      return u;
    }));
  };

  const handleSave = async () => {
    if (!form.customs_office_id) { showToast("انتخاب گمرک الزامی است", false); return; }
    if (form.ownership_type === "transit") {
      if (!form.transit_owner.trim()) { showToast("نام صاحب جنس برای ترانزیت الزامی است", false); return; }
      if (!form.bl_number.trim()) { showToast("شماره BL برای ترانزیت الزامی است", false); return; }
    }
    if (form.status === "cleared" && !form.clearance_date) {
      showToast("تاریخ ترخیص برای وضعیت ترخیص شده الزامی است", false);
      return;
    }
    
    // 🆕 اگر وضعیت ترخیص شده باشد و قبلاً خرید خودکار نداریم، فروشنده لازم است
    const isChangingToCleared = form.status === "cleared" && !hasAutoPurchase;
    if (isChangingToCleared && !autoVendorId) {
      showToast("برای ثبت با وضعیت ترخیص شده، انتخاب فروشنده برای خرید خودکار الزامی است", false);
      return;
    }

    const validItems = items.filter(it => it.product_id && parseFloat(it.quantity) > 0);
    if (validItems.length === 0) { showToast("حداقل یک جنس باید اضافه شود", false); return; }

    const office = offices.find((o: any) => String(o.id) === form.customs_office_id);

    const data: any = {
      ...form,
      customs_office_id: parseInt(form.customs_office_id),
      customs_office_name: office?.name || form.customs_office_name,
      clearance_date: form.clearance_date || null,
      items: validItems.map(it => ({
        product_id: parseInt(it.product_id),
        product_name: it.product_name,
        unit: it.unit,
        quantity: parseFloat(it.quantity),
        notes: it.notes,
      })),
    };
    
    // 🆕 اگر فروشنده انتخاب شده، آن را برای خرید خودکار بفرست
    if (isChangingToCleared && autoVendorId) {
      data.auto_vendor_id = parseInt(autoVendorId);
    }

    let res;
    if (isEdit) {
      res = await trackingAPI.updateShipment({ id: shipmentId, ...data });
    } else {
      res = await trackingAPI.addShipment(data);
    }

    if (res.success) {
      showToast(isEdit ? "بار ویرایش شد" : `بار ${res.serial} ثبت شد`);
      setTimeout(onBack, 800);
    } else {
      showToast(res.message || "خطا", false);
    }
  };

  const productOptions = products.map((p: any) => {
    const u = units.find((un: any) => un.id === p.unit_id);
    return { value: String(p.id), label: `📦 ${p.name}${u ? ` (${u.name})` : ""}` };
  });

  if (loading) {
    return <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>;
  }

  const inp: React.CSSProperties = {
    width: "100%", padding: "10px 14px", borderRadius: 10,
    border: `1.5px solid ${T.cardBorder}`,
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl", outline: "none", boxSizing: "border-box",
  };
  const lbl: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5,
  };
  const card: React.CSSProperties = {
    padding: 22, borderRadius: 14,
    background: dark ? "rgba(255,255,255,0.03)" : "#fff",
    border: `1px solid ${T.cardBorder}`,
    marginBottom: 18,
  };

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "12px 24px", borderRadius: 12, background: toast.ok ? "#10b981" : "#ef4444", color: "#fff", fontWeight: 700, fontSize: 13, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          {toast.text}
        </div>
      )}

      {/* هدر */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: "transparent", color: T.subtext, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>
          <ArrowRight size={15} /> بازگشت
        </button>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.text }}>
          {isEdit ? "ویرایش بار" : "ثبت بار جدید"}
        </h2>
      </div>

      {hasDistributions && (
        <div style={{ padding: "12px 16px", borderRadius: 10, background: "rgba(245,158,11,0.1)", border: "1px solid #f59e0b40", color: "#b45309", fontSize: 12, fontWeight: 700, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
          <AlertTriangle size={16} />
          این بار توزیع‌هایی دارد. اگر اجناس را تغییر دهید، توزیع‌ها سازگار نخواهند بود. اطلاعات اصلی بار قابل ویرایش است.
        </div>
      )}

      {/* اطلاعات کلی بار */}
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>📋 اطلاعات کلی بار</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <div>
            <label style={lbl}>گمرک *</label>
            <SearchableSelect
              options={offices.map((o: any) => ({ value: String(o.id), label: o.name }))}
              value={form.customs_office_id}
              onChange={v => {
                const o = offices.find((x: any) => String(x.id) === v);
                setForm({ ...form, customs_office_id: v, customs_office_name: o?.name || "" });
              }}
              placeholder="انتخاب گمرک"
            />
          </div>
          <div>
            <label style={lbl}>تاریخ ورود به گمرک *</label>
            <PersianDatePicker value={form.entry_date} onChange={v => setForm({ ...form, entry_date: v })} />
          </div>
          <div>
            <label style={lbl}>شماره بار</label>
            <input value={form.bar_number} onChange={e => setForm({ ...form, bar_number: e.target.value })} placeholder="مثلاً: B-1234" style={inp} />
          </div>
        </div>

        {/* 🆕 ردیف شماره اینوایس */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginTop: 14 }}>
          <div>
            <label style={lbl}>شماره اینوایس</label>
            <input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} placeholder="مثلاً: INV-2026-001" style={inp} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginTop: 14 }}>
          <div>
            <label style={lbl}>نوع مالکیت</label>
            <SearchableSelect
              options={OWNERSHIP_OPTIONS}
              value={form.ownership_type}
              onChange={v => setForm({ ...form, ownership_type: v, transit_owner: v === "company" ? "" : form.transit_owner, bl_number: v === "company" ? "" : form.bl_number })}
              placeholder="انتخاب نوع"
            />
          </div>
          <div>
            <label style={lbl}>وضعیت</label>
            <SearchableSelect options={STATUS_OPTIONS} value={form.status} onChange={v => setForm({ ...form, status: v })} placeholder="انتخاب وضعیت" />
          </div>
          <div>
            <label style={lbl}>تاریخ ترخیص {form.status === "cleared" && "*"}</label>
            <PersianDatePicker value={form.clearance_date} onChange={v => setForm({ ...form, clearance_date: v })} />
          </div>
        </div>

        {/* 🆕 اطلاع‌رسانی و انتخاب فروشنده برای خرید خودکار */}
        {form.status === "cleared" && !hasAutoPurchase && (
          <div style={{ marginTop: 14, padding: 14, borderRadius: 10, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 16 }}>🚚</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#d97706" }}>
                خرید خودکار از این بار
              </span>
            </div>
            <div style={{ fontSize: 11, color: T.subtext, marginBottom: 12, lineHeight: 1.7 }}>
              با ثبت بار به وضعیت <strong>«ترخیص شده»</strong>، یک خرید جدید با همین اقلام و وضعیت <strong>«در انتظار»</strong> ساخته می‌شود.
              <br />
              قیمت اقلام در حالت اولیه <strong>صفر</strong> است؛ بعداً می‌توانید از منوی جورنال → خرید‌ها، نرخ‌ها را وارد کرده و خرید را تأیید کنید.
            </div>
            <label style={lbl}>فروشنده برای خرید خودکار *</label>
            <SearchableSelect
              options={vendors.map((v: any) => ({
                value: String(v.id),
                label: `🏢 ${[v.first_name, v.last_name, v.company_name].filter(Boolean).join(' ')}${v.currency ? ` (${v.currency})` : ''}`
              }))}
              value={autoVendorId}
              onChange={v => setAutoVendorId(v)}
              placeholder="انتخاب فروشنده..."
            />
          </div>
        )}
        {form.status === "cleared" && hasAutoPurchase && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.3)" }}>
            <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 700 }}>
              ✓ خرید خودکار قبلاً برای این بار ساخته شده است (در منوی جورنال → خرید‌ها قابل دسترسی)
            </div>
          </div>
        )}

        {form.ownership_type === "transit" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14, padding: 14, borderRadius: 10, background: "rgba(168,85,247,0.05)", border: "1px solid rgba(168,85,247,0.2)" }}>
            <div>
              <label style={lbl}>صاحب جنس (ترانزیت) *</label>
              <input value={form.transit_owner} onChange={e => setForm({ ...form, transit_owner: e.target.value })} placeholder="نام صاحب جنس" style={inp} />
            </div>
            <div>
              <label style={lbl}>شماره BL *</label>
              <input value={form.bl_number} onChange={e => setForm({ ...form, bl_number: e.target.value })} placeholder="BL Number" style={inp} />
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginTop: 14 }}>
          <div>
            <label style={lbl}>آی نمبر</label>
            <input value={form.i_number} onChange={e => setForm({ ...form, i_number: e.target.value })} placeholder="I Number" style={inp} />
          </div>
          <div>
            <label style={lbl}>ای نمبر</label>
            <input value={form.e_number} onChange={e => setForm({ ...form, e_number: e.target.value })} placeholder="E Number" style={inp} />
          </div>
          <div>
            <label style={lbl}>شماره کانتینر</label>
            <input value={form.container_number} onChange={e => setForm({ ...form, container_number: e.target.value })} placeholder="Container #" style={inp} />
          </div>
          <div>
            <label style={lbl}>پلیت موتر</label>
            <input value={form.truck_plate} onChange={e => setForm({ ...form, truck_plate: e.target.value })} placeholder="پلیت" style={inp} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
          <div>
            <label style={lbl}>توضیحات</label>
            <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="توضیحات..." style={inp} />
          </div>
          <div>
            <label style={lbl}>ضمیمه (لینک)</label>
            <input value={form.attachment} onChange={e => setForm({ ...form, attachment: e.target.value })} placeholder="لینک عکس یا فایل" style={inp} />
          </div>
        </div>
      </div>

      {/* اجناس بار */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>📦 اجناس داخل بار</div>
          {!hasDistributions && (
            <button onClick={addItemRow} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: 9, border: "none",
              background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff",
              cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
            }}>
              <Plus size={13} /> افزودن جنس
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div style={{ textAlign: "center", padding: 30, color: T.subtext, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderRadius: 10 }}>
            هیچ جنسی اضافه نشده
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((it, i) => (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "3fr 100px 130px 2fr 40px",
                gap: 10, alignItems: "end",
                padding: 12, borderRadius: 10,
                background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                border: `1px solid ${T.cardBorder}`,
              }}>
                <div>
                  {i === 0 && <label style={lbl}>جنس * (از گدام)</label>}
                  <SearchableSelect
                    options={productOptions}
                    value={it.product_id}
                    onChange={v => updateItem(i, "product_id", v)}
                    placeholder="انتخاب جنس"
                  />
                </div>
                <div>
                  {i === 0 && <label style={lbl}>واحد</label>}
                  <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.cardBorder}`, color: T.text, fontSize: 13, minHeight: 40, display: "flex", alignItems: "center" }}>
                    {it.unit || "—"}
                  </div>
                </div>
                <div>
                  {i === 0 && <label style={lbl}>مقدار *</label>}
                  <input type="number" value={it.quantity} onChange={e => updateItem(i, "quantity", e.target.value)} placeholder="0" style={inp} />
                </div>
                <div>
                  {i === 0 && <label style={lbl}>یادداشت</label>}
                  <input value={it.notes} onChange={e => updateItem(i, "notes", e.target.value)} placeholder="..." style={inp} />
                </div>
                <div style={{ marginTop: i === 0 ? 22 : 0 }}>
                  {!hasDistributions && items.length > 1 && (
                    <button onClick={() => removeItemRow(i)} style={{ width: 36, height: 40, borderRadius: 9, border: "none", background: "rgba(239,68,68,0.15)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* دکمه‌های ذخیره */}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}` }}>
        <button onClick={onBack} style={{ padding: "11px 24px", borderRadius: 11, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
          انصراف
        </button>
        <button onClick={() => submit(handleSave)} disabled={submitting} style={{
          display: "flex", alignItems: "center", gap: 7,
          padding: "11px 28px", borderRadius: 11, border: "none",
          background: submitting ? "#94a3b8" : "linear-gradient(135deg,#3b82f6,#2563eb)",
          color: "#fff", cursor: submitting ? "not-allowed" : "pointer",
          fontSize: 13, fontWeight: 800, fontFamily: "inherit",
          boxShadow: submitting ? "none" : "0 4px 14px rgba(59,130,246,0.4)",
        }}>
          <Save size={15} />
          {submitting ? "در حال ذخیره..." : (isEdit ? "ذخیره تغییرات" : "ثبت بار")}
        </button>
      </div>
    </div>
  );
}